import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Bell, 
  AlertTriangle, 
  AlertCircle,
  CheckCircle,
  Clock,
  Settings,
  Filter,
  RefreshCw,
  ExternalLink,
  X,
  Info,
  Zap,
  Shield,
  Database,
  TrendingUp,
  Eye,
  EyeOff,
  ChevronRight
} from 'lucide-react';
import { API } from '../App';
import { apiFetch } from '../services/api_service';


const AlertsCenter = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alerts');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [alertStats, setAlertStats] = useState({ critical: 0, high: 0, medium: 0, low: 0 });
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [acknowledgedAlerts, setAcknowledgedAlerts] = useState(new Set());

  useEffect(() => {
    fetchAlerts();
    fetchConfigurations();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/alerts/active`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setAlerts(data.alerts || []);
      setAlertStats({
        critical: data.critical_count || 0,
        high: data.high_count || 0,
        medium: data.alerts?.filter(a => a.severity === 'medium').length || 0,
        low: data.alerts?.filter(a => a.severity === 'low').length || 0
      });
    } catch (error) {
      console.error('Error fetching alerts:', error);
      // Set mock data for demo
      const mockAlerts = [
        {
          id: "alert_csrd_deadline",
          type: "deadline",
          severity: "critical",
          title: "CSRD Reporting Deadline Approaching",
          message: "CSRD annual report due in 45 days",
          metric: "days_to_deadline",
          current_value: 45,
          threshold: 60,
          timestamp: new Date().toISOString(),
          actionable: true,
          action_url: "/obligations/deadlines"
        },
        {
          id: "alert_audit_deadline",
          type: "deadline",
          severity: "high",
          title: "External Audit Scheduled",
          message: "Annual ESG audit begins in 30 days",
          metric: "days_to_audit",
          current_value: 30,
          threshold: 45,
          timestamp: new Date().toISOString(),
          actionable: true,
          action_url: "/audit/trail"
        },
        {
          id: "alert_scope3_increase",
          type: "emissions",
          severity: "medium",
          title: "Scope 3 Emissions Trend Alert",
          message: "Scope 3 emissions increased 12% vs. previous quarter",
          metric: "scope3_emissions",
          current_value: 45890,
          threshold: 40000,
          change_percent: 12.3,
          timestamp: new Date().toISOString(),
          actionable: true,
          action_url: "/emissions-tracking"
        },
        {
          id: "alert_data_quality",
          type: "data_quality",
          severity: "low",
          title: "Data Freshness Warning",
          message: "3 data sources haven't been updated in 30+ days",
          metric: "stale_sources",
          current_value: 3,
          threshold: 5,
          timestamp: new Date().toISOString(),
          actionable: true,
          action_url: "/data-upload"
        }
      ];
      setAlerts(mockAlerts);
      setAlertStats({
        critical: mockAlerts.filter(a => a.severity === 'critical').length,
        high: mockAlerts.filter(a => a.severity === 'high').length,
        medium: mockAlerts.filter(a => a.severity === 'medium').length,
        low: mockAlerts.filter(a => a.severity === 'low').length
      });
    }
    setLoading(false);
  };

  const fetchConfigurations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/alerts/configurations`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await response.json();
      setConfigurations(data.configurations || []);
    } catch (error) {
      console.error('Error fetching configurations:', error);
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/alerts/${alertId}/dismiss`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setDismissedAlerts(prev => new Set([...prev, alertId]));
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setAcknowledgedAlerts(prev => new Set([...prev, alertId]));
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const getSeverityConfig = (severity) => {
    const configs = {
      critical: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', icon: AlertCircle, badge: 'bg-red-600' },
      high: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-800', icon: AlertTriangle, badge: 'bg-orange-600' },
      medium: { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-800', icon: Info, badge: 'bg-yellow-600' },
      low: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', icon: Info, badge: 'bg-blue-600' }
    };
    return configs[severity] || configs.low;
  };

  const getTypeIcon = (type) => {
    const icons = {
      compliance: Shield,
      emissions: TrendingUp,
      deadline: Clock,
      data_quality: Database
    };
    return icons[type] || Bell;
  };

  const filteredAlerts = alerts.filter(alert => {
    if (dismissedAlerts.has(alert.id)) return false;
    if (filterSeverity === 'all') return true;
    return alert.severity === filterSeverity;
  });

  return (
    <div className="space-y-6" data-testid="alerts-center">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Alerts & Notifications</h1>
            <p className="text-sm text-slate-500">Monitor compliance thresholds and system alerts</p>
          </div>
        </div>
        <button
          onClick={fetchAlerts}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          data-testid="refresh-alerts-btn"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Alert Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Critical', count: alertStats.critical, color: 'red' },
          { label: 'High', count: alertStats.high, color: 'orange' },
          { label: 'Medium', count: alertStats.medium, color: 'yellow' },
          { label: 'Low', count: alertStats.low, color: 'blue' }
        ].map(({ label, count, color }) => (
          <div
            key={label}
            onClick={() => setFilterSeverity(label.toLowerCase())}
            className={`p-4 rounded-xl border cursor-pointer transition-all ${
              filterSeverity === label.toLowerCase() 
                ? `bg-${color}-50 border-${color}-300 ring-2 ring-${color}-500` 
                : 'bg-white border-slate-200 hover:border-slate-300'
            }`}
            data-testid={`alert-stat-${label.toLowerCase()}`}
          >
            <div className={`text-3xl font-bold text-${color}-600`}>{count}</div>
            <div className="text-sm text-slate-600">{label} Alerts</div>
          </div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-4 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('alerts')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'alerts' 
              ? 'border-emerald-600 text-emerald-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-alerts"
        >
          Active Alerts ({filteredAlerts.length})
        </button>
        <button
          onClick={() => setActiveTab('configurations')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'configurations' 
              ? 'border-emerald-600 text-emerald-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-configurations"
        >
          Alert Configurations
        </button>
      </div>

      {/* Filter Bar */}
      {activeTab === 'alerts' && (
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Filter size={16} />
            Filter by severity:
          </div>
          <div className="flex gap-2">
            {['all', 'critical', 'high', 'medium', 'low'].map(sev => (
              <button
                key={sev}
                onClick={() => setFilterSeverity(sev)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  filterSeverity === sev
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${sev}`}
              >
                {sev.charAt(0).toUpperCase() + sev.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content Area */}
      {activeTab === 'alerts' ? (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw size={32} className="mx-auto text-slate-400 animate-spin" />
              <p className="mt-2 text-slate-500">Loading alerts...</p>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <CheckCircle size={48} className="mx-auto text-emerald-500" />
              <h3 className="mt-4 text-lg font-medium text-slate-900">No Active Alerts</h3>
              <p className="text-slate-500">All systems are operating within normal parameters.</p>
            </div>
          ) : (
            filteredAlerts.map(alert => {
              const config = getSeverityConfig(alert.severity);
              const TypeIcon = getTypeIcon(alert.type);
              const SeverityIcon = config.icon;
              const isAcknowledged = acknowledgedAlerts.has(alert.id);

              return (
                <div
                  key={alert.id}
                  className={`p-4 rounded-xl border ${config.bg} ${config.border} ${
                    isAcknowledged ? 'opacity-75' : ''
                  }`}
                  data-testid={`alert-${alert.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${config.badge} text-white`}>
                        <TypeIcon size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${config.badge} text-white`}>
                            {alert.severity.toUpperCase()}
                          </span>
                          <span className="text-xs text-slate-500">
                            {alert.type.replace('_', ' ').toUpperCase()}
                          </span>
                          {isAcknowledged && (
                            <span className="px-2 py-0.5 text-xs bg-slate-200 text-slate-600 rounded-full">
                              Acknowledged
                            </span>
                          )}
                        </div>
                        <h3 className={`font-semibold ${config.text}`}>{alert.title}</h3>
                        <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                        
                        {/* Metrics */}
                        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                          {alert.current_value !== undefined && (
                            <span>Current: <strong className={config.text}>{alert.current_value}</strong></span>
                          )}
                          {alert.threshold !== undefined && (
                            <span>Threshold: <strong>{alert.threshold}</strong></span>
                          )}
                          {alert.change_percent !== undefined && (
                            <span>Change: <strong className={alert.change_percent > 0 ? 'text-red-600' : 'text-green-600'}>
                              {alert.change_percent > 0 ? '+' : ''}{alert.change_percent}%
                            </strong></span>
                          )}
                        </div>

                        <div className="mt-2 text-xs text-slate-400">
                          <Clock size={12} className="inline mr-1" />
                          {new Date(alert.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {alert.actionable && alert.action_url && (
                        <button
                          onClick={() => navigate(alert.action_url)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          data-testid={`alert-action-${alert.id}`}
                        >
                          View <ExternalLink size={14} />
                        </button>
                      )}
                      {!isAcknowledged && (
                        <button
                          onClick={() => handleAcknowledge(alert.id)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Acknowledge"
                          data-testid={`acknowledge-${alert.id}`}
                        >
                          <Eye size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDismiss(alert.id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                        title="Dismiss"
                        data-testid={`dismiss-${alert.id}`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        /* Configurations Tab */
        <div className="space-y-4">
          {configurations.map(config => (
            <div
              key={config.id}
              className="p-6 bg-white rounded-xl border border-slate-200"
              data-testid={`config-${config.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{config.name}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      config.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {config.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{config.description}</p>
                </div>
                <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                  <Settings size={18} />
                </button>
              </div>

              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Thresholds</h4>
                {config.thresholds.map((threshold, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        threshold.enabled ? 'bg-emerald-500' : 'bg-slate-300'
                      }`} />
                      <span className="text-sm text-slate-700">
                        {threshold.metric_name.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-slate-500">
                        {threshold.threshold_type === 'above' && '>'}
                        {threshold.threshold_type === 'below' && '<'}
                        {threshold.threshold_type === 'change_percent' && '±'}
                        {threshold.threshold_value}{threshold.threshold_type === 'change_percent' && '%'}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        threshold.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        threshold.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                        threshold.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {threshold.severity}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Bell size={12} />
                  Notifications: {config.notification_channels.join(', ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsCenter;
